
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Shield, Users, Calendar, TrendingUp, Ban, CheckCircle, Settings, Tag, Activity, BarChart3, UserCheck, Clock } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SystemSettings } from "@/components/SystemSettings";
import { PromoCodeManager } from "@/components/PromoCodeManager";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from "recharts";

interface EnhancedStats {
  totalEvents: number;
  totalUsers: number;
  activeEvents: number;
  totalVotes: number;
  totalGuests: number;
  averageGuestsPerEvent: number;
  eventCompletionRate: number;
  activeUsersLast30Days: number;
}

interface EventData {
  id: string;
  name: string;
  host_name: string;
  created_at: string;
  is_active: boolean;
  deadline: string | null;
  total_votes: number;
  total_guests: number;
  user_id: string;
}

interface UserActivity {
  user_id: string;
  display_name: string | null;
  email: string;
  events_created: number;
  total_votes_received: number;
  total_guests_invited: number;
  last_event_created: string | null;
}

interface ActivityTrend {
  date: string;
  events_created: number;
  votes_cast: number;
  users_active: number;
}

interface AdminAnalytics {
  stats: EnhancedStats;
  events: EventData[];
  topUsers: UserActivity[];
  activityTrends: ActivityTrend[];
  metrics: {
    eventsByType: { type: string; count: number }[];
    userGrowth: { month: string; new_users: number }[];
    engagementMetrics: {
      events_with_votes: number;
      events_without_votes: number;
      average_response_time_hours: number;
    };
    subscriptionMetrics: {
      total_users: number;
      free_users: number;
      paid_users: number;
      conversion_rate: number;
      subscription_distribution: { tier: string; count: number }[];
    };
  };
}

interface UserData {
  id: string;
  email: string;
  display_name: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  events_created: number;
  is_admin: boolean;
}

export const AdminDashboard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))'];

  useEffect(() => {
    checkAdminStatus();
  }, [user]);

  useEffect(() => {
    if (isAdmin) {
      loadAdminData();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  const checkAdminStatus = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Use the security definer function to check admin role
      const { data, error } = await supabase.rpc('check_user_role', {
        user_uuid: user.id,
        role_name: 'admin'
      });

      if (error) {
        throw error;
      }

      setIsAdmin(data === true);
    } catch (error) {
      console.log('Error checking admin status:', error);
      setIsAdmin(false);
    } finally {
      if (!isAdmin) {
        setLoading(false);
      }
    }
  };

  const loadAdminData = async () => {
    try {
      console.log('Loading optimized admin analytics...');
      const { data, error } = await supabase.functions.invoke('get-admin-analytics');
      
      if (error) throw error;
      
      console.log('Analytics data loaded:', data);
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading admin analytics:', error);
      toast({
        title: "Error",
        description: "Failed to load admin dashboard data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    if (!isAdmin) {
      console.log('Not admin, cannot load users');
      return;
    }
    
    console.log('Starting to load users...');
    setUsersLoading(true);
    try {
      console.log('Calling get-admin-users function...');
      const { data, error } = await supabase.functions.invoke('get-admin-users');
      
      console.log('Function response:', { data, error });
      
      if (error) {
        console.error('Function error:', error);
        throw error;
      }
      
      console.log('Setting users data:', data);
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast({
        title: "Error",
        description: "Failed to load user data.",
        variant: "destructive",
      });
    } finally {
      setUsersLoading(false);
    }
  };

  const toggleEventStatus = async (eventId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('events')
        .update({ is_active: !currentStatus })
        .eq('id', eventId);

      if (error) throw error;

      // Update the local analytics data
      if (analytics) {
        const updatedEvents = analytics.events.map(event => 
          event.id === eventId 
            ? { ...event, is_active: !currentStatus }
            : event
        );
        
        setAnalytics({
          ...analytics,
          events: updatedEvents,
          stats: {
            ...analytics.stats,
            activeEvents: updatedEvents.filter(e => e.is_active).length,
          }
        });
      }

      toast({
        title: "Event updated",
        description: `Event has been ${!currentStatus ? 'activated' : 'deactivated'}.`,
      });
    } catch (error) {
      console.error('Error updating event status:', error);
      toast({
        title: "Error",
        description: "Failed to update event status.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-background">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">Loading admin dashboard...</div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <CardTitle>Admin Access Required</CardTitle>
            <CardDescription>
              You don't have permission to access the admin dashboard.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="w-8 h-8" />
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground">System overview and management tools</p>
        </div>

        {/* Enhanced Stats Overview */}
        <div className="grid gap-4 md:grid-cols-4 lg:grid-cols-8 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Total Events
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics?.stats.totalEvents || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                Total Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics?.stats.totalUsers || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Active Events
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics?.stats.activeEvents || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Total Votes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics?.stats.totalVotes || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <UserCheck className="w-4 h-4" />
                Total Guests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics?.stats.totalGuests || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Avg Guests/Event
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics?.stats.averageGuestsPerEvent || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Completion Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics?.stats.eventCompletionRate || 0}%</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Active (30d)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics?.stats.activeUsersLast30Days || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                Free Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics?.metrics.subscriptionMetrics.free_users || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-accent" />
                Paid Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-accent">{analytics?.metrics.subscriptionMetrics.paid_users || 0}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {analytics?.metrics.subscriptionMetrics.conversion_rate || 0}% conversion
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Management Tabs */}
        <Tabs defaultValue="events" onValueChange={(value) => {
          if (value === "users" && users.length === 0 && !usersLoading) {
            loadUsers();
          }
        }}>
          <TabsList>
            <TabsTrigger value="events">Event Management</TabsTrigger>
            <TabsTrigger value="analytics">Advanced Analytics</TabsTrigger>
            <TabsTrigger value="users">User Management</TabsTrigger>
            <TabsTrigger value="promo">Promo Codes</TabsTrigger>
            <TabsTrigger value="settings">System Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="events">
            <Card>
              <CardHeader>
                <CardTitle>Event Management</CardTitle>
                <CardDescription>View and manage all events in the system</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event Name</TableHead>
                      <TableHead>Host</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Votes</TableHead>
                      <TableHead>Guests</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics?.events.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell className="font-medium">{event.name}</TableCell>
                        <TableCell>{event.host_name}</TableCell>
                        <TableCell>
                          {new Date(event.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant={event.is_active ? "default" : "secondary"}>
                            {event.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>{event.total_votes}</TableCell>
                        <TableCell>{event.total_guests}</TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleEventStatus(event.id, event.is_active)}
                          >
                            {event.is_active ? (
                              <>
                                <Ban className="w-3 h-3 mr-1" />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Activate
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    )) || []}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Activity Trends Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Activity Trends (Last 30 Days)</CardTitle>
                  <CardDescription>Daily events created, votes cast, and active users</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analytics?.activityTrends || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="events_created" stroke="hsl(var(--primary))" name="Events Created" />
                      <Line type="monotone" dataKey="votes_cast" stroke="hsl(var(--secondary))" name="Votes Cast" />
                      <Line type="monotone" dataKey="users_active" stroke="hsl(var(--accent))" name="Active Users" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <div className="grid gap-6 md:grid-cols-2">
                {/* Most Active Users */}
                <Card>
                  <CardHeader>
                    <CardTitle>Most Active Users</CardTitle>
                    <CardDescription>Top event creators by activity</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {analytics?.topUsers.slice(0, 5).map((user, index) => (
                        <div key={user.user_id} className="flex items-center justify-between p-3 rounded-lg bg-muted">
                          <div>
                            <div className="font-medium">{user.display_name || 'Anonymous'}</div>
                            <div className="text-sm text-muted-foreground">
                              {user.events_created} events • {user.total_votes_received} votes received
                            </div>
                          </div>
                          <Badge variant="outline">#{index + 1}</Badge>
                        </div>
                      )) || []}
                    </div>
                  </CardContent>
                </Card>

                {/* Event Types Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle>Event Types</CardTitle>
                    <CardDescription>Distribution by event type</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={analytics?.metrics.eventsByType || []}
                          cx="50%"
                          cy="50%"
                          outerRadius={60}
                          fill="hsl(var(--primary))"
                          dataKey="count"
                          label={({ type, count }) => `${type}: ${count}`}
                        >
                          {analytics?.metrics.eventsByType.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Engagement Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle>Engagement Metrics</CardTitle>
                  <CardDescription>Event participation and response analytics</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="p-4 rounded-lg bg-muted">
                      <div className="text-2xl font-bold">{analytics?.metrics.engagementMetrics.events_with_votes || 0}</div>
                      <div className="text-sm text-muted-foreground">Events with Votes</div>
                    </div>
                    <div className="p-4 rounded-lg bg-muted">
                      <div className="text-2xl font-bold">{analytics?.metrics.engagementMetrics.events_without_votes || 0}</div>
                      <div className="text-sm text-muted-foreground">Events without Votes</div>
                    </div>
                    <div className="p-4 rounded-lg bg-muted">
                      <div className="text-2xl font-bold">{analytics?.metrics.engagementMetrics.average_response_time_hours || 0}h</div>
                      <div className="text-sm text-muted-foreground">Avg Response Time</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* User Growth Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>User Growth (Last 12 Months)</CardTitle>
                  <CardDescription>New user registrations by month</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics?.metrics.userGrowth || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="new_users" fill="hsl(var(--primary))" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Subscription Analytics */}
              <Card>
                <CardHeader>
                  <CardTitle>Subscription Metrics</CardTitle>
                  <CardDescription>Free vs paid user distribution and conversion analytics</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2 mb-6">
                    <div className="space-y-4">
                      <div className="p-4 rounded-lg bg-muted">
                        <div className="text-2xl font-bold text-primary">{analytics?.metrics.subscriptionMetrics.free_users || 0}</div>
                        <div className="text-sm text-muted-foreground">Free Tier Users</div>
                      </div>
                      <div className="p-4 rounded-lg bg-muted">
                        <div className="text-2xl font-bold text-accent">{analytics?.metrics.subscriptionMetrics.paid_users || 0}</div>
                        <div className="text-sm text-muted-foreground">Paid Users</div>
                      </div>
                      <div className="p-4 rounded-lg bg-primary/10">
                        <div className="text-2xl font-bold text-primary">{analytics?.metrics.subscriptionMetrics.conversion_rate || 0}%</div>
                        <div className="text-sm text-muted-foreground">Conversion Rate (Free → Paid)</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium mb-4">Subscription Distribution</div>
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={analytics?.metrics.subscriptionMetrics.subscription_distribution || []}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ tier, count, percent }) => `${tier}: ${count} (${(percent * 100).toFixed(0)}%)`}
                            outerRadius={80}
                            fill="hsl(var(--primary))"
                            dataKey="count"
                          >
                            {analytics?.metrics.subscriptionMetrics.subscription_distribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>User Management - Updated {new Date().toLocaleTimeString()}</CardTitle>
                <CardDescription>View all registered users and their activity</CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  console.log('User Management render state:', { 
                    usersLoading, 
                    usersLength: users.length, 
                    users: users.slice(0, 2) // Show first 2 users for debugging
                  });
                  return null;
                })()}
                {usersLoading ? (
                  <div className="text-center py-8">Loading user data...</div>
                ) : users.length === 0 ? (
                  <div className="text-center py-8">
                    <Button onClick={loadUsers} variant="outline">
                      Load Users
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Display Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead>Last Login</TableHead>
                        <TableHead>Events Created</TableHead>
                        <TableHead>Role</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((userData) => (
                        <TableRow key={userData.id}>
                          <TableCell className="font-medium">
                            {userData.display_name || 'No name set'}
                          </TableCell>
                          <TableCell>{userData.email}</TableCell>
                          <TableCell>
                            {new Date(userData.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {userData.last_sign_in_at 
                              ? new Date(userData.last_sign_in_at).toLocaleDateString()
                              : 'Never'
                            }
                          </TableCell>
                          <TableCell>{userData.events_created}</TableCell>
                          <TableCell>
                            <Badge variant={userData.is_admin ? "destructive" : "secondary"}>
                              {userData.is_admin ? "Admin" : "User"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="promo">
            <PromoCodeManager />
          </TabsContent>

          <TabsContent value="settings">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    System Settings
                  </CardTitle>
                  <CardDescription>
                    Configure system-wide features and settings
                  </CardDescription>
                </CardHeader>
              </Card>
              
              <SystemSettings />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
